<template>
    <v-form ref="experimentDesignConfigForm">
        <v-col cols="12">
            <v-row>
                <v-col cols="2" v-if="!_.isNil(numOfConditions)">
                    <v-text-field
                        label="# of Conditions"
                        :model-value="numOfConditions"
                        :disabled="disabled"
                        readonly
                    />
                </v-col>
                <v-col cols="4">
                    <v-autocomplete
                        v-model="selectedContainerTypeId"
                        :items="containerTypeOptions"
                        item-title="name"
                        item-value="id"
                        label="Container Type (*)"
                        clearable
                        @update:modelValue="onChanged"
                        :rules="requiredSimpleValueRules('Container type')"
                        :disabled="disabled"
                    />
                </v-col>
                <v-col cols="2">
                    <v-text-field
                        v-model="numberOfReplicates"
                        label="# of Replicates (*)"
                        type="number"
                        @update:modelValue="onChanged"
                        :rules="requiredSimpleValueRules('# of Replicates')"
                        :disabled="disabled"
                    />
                </v-col>
                <v-col v-if="displayPlateLayoutSelection" cols="3">
                    Select packing direction:
                    <v-radio-group inline v-model="rowColumnLayout" @update:modelValue="onChanged" :disabled="disabled">
                        <v-radio label="Horizontal" value="horizontal"></v-radio>
                        <v-radio label="Vertical" value="vertical"></v-radio>
                    </v-radio-group>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-autocomplete
                        v-model="experimentalUnit"
                        :items="experimentalUnitOptions"
                        label="Experimental Unit (*)"
                        @update:modelValue="onChanged"
                        :rules="requiredSimpleValueRules('Randomization type')"
                        :disabled="disabled"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-autocomplete
                        v-model="randomizationType"
                        :items="randomizationTypeOptions"
                        label="Randomization Type (*)"
                        @update:modelValue="onChanged"
                        :rules="requiredSimpleValueRules('Randomization type')"
                        :disabled="isRandomizationTypeFieldDisabled"
                    />
                </v-col>
                <v-col v-if="randomizationType == 'none'" cols="6" align-self="center">
                    <v-autocomplete
                        v-model="conditionAssignmentStrategy"
                        :items="conditionAssignmentStrategyOptions"
                        label="Condition Assignment Strategy (*)"
                        @update:modelValue="onChanged"
                        :rules="requiredSimpleValueRules('Condition assignment strategy')"
                        :disabled="disabled"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-form>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { requiredSimpleValueRules } from "@/lib/validationUtils";
import { registriesAPI } from "@/services/registries/registriesAPI";
import { EventBus } from "@/lib/event-bus";
import _ from "lodash";
import {
    ExperimentalUnitOption,
    ExperimentDesignConfig,
} from "@/services/experimentDesignConfigs/experimentDesignConfigsAPI";
import {
    ConditionAssignmentStrategy,
    ContainerType,
    ExperimentalUnit,
    PlateLayout,
    RandomizationType,
} from "@/services/commonModels";
import { withLoadingScreen } from "@/lib/loading";

const { modelValue, experimentalUnitOptions, disabled, numOfConditions } = defineProps<{
  modelValue: ExperimentDesignConfig | null;
  experimentalUnitOptions: ExperimentalUnitOption[];
  disabled?: boolean;
  numOfConditions?: number | null;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", updatedExperimentDesignConfig: ExperimentDesignConfig): void;
}>();

onMounted(() => {
    fetchContainerTypeOptions();
});

const experimentDesignConfigForm = ref();
const containerTypeOptions = ref<ContainerType[]>([]);

const selectedContainerTypeId = ref<string | null>(null);
const numberOfReplicates = ref<number>(modelValue?.numberOfReplicates ?? 1);
const rowColumnLayout = ref<PlateLayout>(modelValue?.rowColumnLayout ?? "horizontal");
const randomizationType = ref<RandomizationType>(modelValue?.randomizationType ?? "none");
const experimentalUnit = ref<ExperimentalUnit>(modelValue?.experimentalUnit ?? "container");
const conditionAssignmentStrategy = ref<ConditionAssignmentStrategy | null>(modelValue?.conditionAssignmentStrategy ?? null);

watch(() => modelValue, (newValue, _ ) => {
    selectedContainerTypeId.value = newValue?.containerTypeRegistryEntryId ?? null;
    numberOfReplicates.value = newValue?.numberOfReplicates ?? 1;
    rowColumnLayout.value = newValue?.rowColumnLayout ?? "horizontal" as PlateLayout;
    randomizationType.value = newValue?.randomizationType ?? "none" as RandomizationType;
    experimentalUnit.value = newValue?.experimentalUnit ?? "container" as ExperimentalUnit;
    conditionAssignmentStrategy.value = newValue?.conditionAssignmentStrategy ?? null;
});

function onChanged() {
    enforceRandomizationTypeIfNecessary();
    clearConditionAssignmentStrategyIfNecessary();

    if (selectedContainerTypeId.value) {
        const updatedExperimentDesignConfig = {
            ...modelValue,
            containerTypeRegistryEntryId: selectedContainerTypeId.value,
            numberOfReplicates: numberOfReplicates.value,
            rowColumnLayout: rowColumnLayout.value,
            randomizationType: randomizationType.value,
            experimentalUnit: experimentalUnit.value,
            conditionAssignmentStrategy: conditionAssignmentStrategy.value,
        }
        emit("update:modelValue", updatedExperimentDesignConfig);
    }
}

const isRandomizationTypeFieldDisabled = computed(() => {
    return experimentalUnit.value == "well" || disabled == true;
})

function enforceRandomizationTypeIfNecessary() {
    if (experimentalUnit.value == "well") {
        randomizationType.value = "none";
    }
}

function clearConditionAssignmentStrategyIfNecessary() {
    if (randomizationType.value != "none") {
        conditionAssignmentStrategy.value = null;
    }
}

async function validate() {
  const form = experimentDesignConfigForm.value;
  const validationResult = await form.validate();
  if (!validationResult.valid) {
    const errors = validationResult.errors;
    const msgs = errors.map((error) => {
      return error.errorMessages.join(", ");
    });
    EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
    return false;
  } else {
    return true;
  }
}

const displayPlateLayoutSelection = computed(() => {
  return (
    selectedContainerTypeId.value &&
    (containerTypeOptions.value.find((type) => type.id === selectedContainerTypeId.value)
      ?.numberOfWells ?? 0) > 1
  );
});

const randomizationTypeOptions = [
    {
        title: "None",
        value: "none",
    },
    {
        title: "Randomized Complete Block",
        value: "randomized_complete_block",
    }
];

const conditionAssignmentStrategyOptions = [
    {
        title: "Sequential by Condition",
        value: "sequential_by_condition",
    },
    {
        title: "Sequential by Replicate",
        value: "sequential_by_replicate",
    }
];

defineExpose({
    validate,
});

async function fetchContainerTypeOptions() {
    await withLoadingScreen(async () => {
        const containerTypes = await registriesAPI.fetchRegistryEntriesForSchemaName("container_types");
        containerTypeOptions.value = containerTypes.map((containerType) => {
            return {
                id: containerType.id,
                name: containerType.displayName,
                numberOfWells: (containerType.data["num_wells"] as number) || 0,
            };
        });
    });
    selectedContainerTypeId.value = modelValue?.containerTypeRegistryEntryId ?? null;
}

</script>
